import { useMutation, useQueryClient } from '@tanstack/react-query';

import { assignTags, createTag, deleteTag, renameTag, setCompanyTags } from '../../api/crmApi';
import { companiesKeys } from '../companies/keys';

import { tagsKeys, tagsMutationKeys } from './keys';

/**
 * Invalidate the company caches affected by a tag change: the list/drawer caches (plural
 * companiesKeys) and the detail page caches (singular ['crm','company', id]) for each
 * touched company.
 */
function invalidateCompanies(qc, companyIds = []) {
	qc.invalidateQueries({ queryKey: companiesKeys.all });
	for (const id of companyIds) {
		qc.invalidateQueries({ queryKey: ['crm', 'company', id] });
	}
}

/**
 * Optimistically set a company's tags across every cached company-list query (the table reads
 * from these, keyed by filters, so all matching entries are patched). Returns a [key, prevData]
 * snapshot of the touched caches for rollback in onError.
 */
function patchCompanyTagsInLists(qc, companyId, tags) {
	const previous = [];
	for (const [key, data] of qc.getQueriesData({ queryKey: ['crm', 'companies', 'list'] })) {
		if (!Array.isArray(data?.companies)) continue;
		let touched = false;
		const companies = data.companies.map((c) => {
			if (c.id !== companyId) return c;
			touched = true;
			return { ...c, tags };
		});
		if (touched) {
			previous.push([key, data]);
			qc.setQueryData(key, { ...data, companies });
		}
	}
	return previous;
}

export function useCreateTag() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: tagsMutationKeys.create,
		mutationFn: createTag,
		onSettled: () => {
			qc.invalidateQueries({ queryKey: tagsKeys.all });
		},
	});
}

/**
 * Renames a tag. Invalidates the tag list and all company caches, since the tag name is
 * rendered on company chips throughout the CRM.
 */
export function useRenameTag() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: tagsMutationKeys.rename,
		mutationFn: ({ id, name }) => renameTag(id, { name }),
		onSettled: () => {
			qc.invalidateQueries({ queryKey: tagsKeys.all });
			qc.invalidateQueries({ queryKey: companiesKeys.all });
		},
	});
}

/**
 * Deletes a tag definition (the backend removes it from all companies). Invalidates the tag
 * list and all company caches so the chips disappear everywhere.
 */
export function useDeleteTag() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: tagsMutationKeys.delete,
		mutationFn: (id) => deleteTag(id),
		onSettled: () => {
			qc.invalidateQueries({ queryKey: tagsKeys.all });
			qc.invalidateQueries({ queryKey: companiesKeys.all });
		},
	});
}

export function useAssignTags() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: tagsMutationKeys.assign,
		mutationFn: assignTags,
		onSettled: (data, err, variables) => {
			qc.invalidateQueries({ queryKey: tagsKeys.all });
			invalidateCompanies(qc, variables?.companyIds);
		},
	});
}

/**
 * Replace a company's tags with an optimistic update. Pass the full desired tag objects
 * ({ companyId, tags: [{id,name}] }); both the detail cache's `data.tags` and the company in
 * every cached list query are patched immediately and rolled back if the request fails (so the
 * tag also disappears instantly in the company table). Mirrors the optimistic pattern of useUpdateDeal.
 */
export function useUpdateCompanyTags() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: tagsMutationKeys.setCompany,
		mutationFn: ({ companyId, tags }) =>
			setCompanyTags(
				companyId,
				tags.map((t) => t.id),
			),
		onMutate: async ({ companyId, tags }) => {
			const detailKey = ['crm', 'company', companyId];
			await qc.cancelQueries({ queryKey: detailKey });
			await qc.cancelQueries({ queryKey: ['crm', 'companies', 'list'] });
			const previous = qc.getQueryData(detailKey);
			if (previous) qc.setQueryData(detailKey, { ...previous, tags });
			const previousLists = patchCompanyTagsInLists(qc, companyId, tags);
			return { detailKey, previous, previousLists };
		},
		onError: (err, vars, context) => {
			if (context?.previous) qc.setQueryData(context.detailKey, context.previous);
			for (const [key, data] of context?.previousLists ?? []) {
				qc.setQueryData(key, data);
			}
		},
		onSettled: (data, err, variables) => {
			qc.invalidateQueries({ queryKey: tagsKeys.all });
			invalidateCompanies(qc, variables?.companyId ? [variables.companyId] : []);
		},
	});
}
